import { PageSectionProps } from "@components/page-section/page-section";
import { StorageUsage } from "@pages/settings/subscription-settings/storage-usage";
import { SubscriptionConstraint } from "@pages/settings/subscription-settings/usage-metrics/subscription-constraint";
import { ConstraintLabel } from "@pages/settings/subscription-settings/usage-metrics/constraint-label";
import {
  selectQuotasSelector,
  selectStorageUsedSelector,
} from "@store/sdb-company/sdb-company-selector";
import { useAppSelector } from "@store/store-helper";
import { useMemo } from "react";
import { ConstraintAction } from "@pages/settings/subscription-settings/usage-metrics/constraint-action";
import { FaroCoreApiTypes } from "@stellar/core-api-client";

interface UseUsageMetrics {
  /** Props for the subscription section */
  usageSectionProps: PageSectionProps[];
}

/** Checks if a certain quota was exceeded */
export function hasExceededQuota(
  quota?: FaroCoreApiTypes.ConstraintQuota
): boolean {
  return (quota?.current ?? 0) > (quota?.limit ?? 0);
}

/** Custom hook to return usage metrics props for subscription page */
export function useUsageMetrics(): UseUsageMetrics {
  const selectStorageUsed = useAppSelector(selectStorageUsedSelector);
  const selectQuotas = useAppSelector(selectQuotasSelector);

  const quotasWithoutStorage = selectQuotas.filter(
    ({ identifier }) => identifier !== "storage"
  );

  // Props for the subscription section
  const usageSectionProps: PageSectionProps[] = useMemo(() => {
    const hasQuotas = !!quotasWithoutStorage.length;

    if (!hasQuotas && !selectStorageUsed) {
      return [];
    }

    return [
      {
        title: "Usage metrics",
        subtitle:
          "Your current plan has limits for certain features. This section outlines its usage. You can upgrade your plan at any time to adjust these limits.",
        items: [
          ...quotasWithoutStorage.map((constraint, index) => ({
            label: <ConstraintLabel type={constraint.identifier} />,
            content: (
              <SubscriptionConstraint
                key={index}
                constraint={constraint}
                actionComponent={
                  <ConstraintAction
                    constraintType={constraint.identifier}
                    data={constraint}
                  />
                }
              />
            ),
            shouldHideTopBorder: index === 0,
          })),
          {
            label: <ConstraintLabel type={"storage"} />,
            content: <StorageUsage />,
            isHidden: !selectStorageUsed,
            shouldHideTopBorder: quotasWithoutStorage.length === 0,
          },
        ],
      },
    ];
  }, [quotasWithoutStorage, selectStorageUsed]);

  return { usageSectionProps };
}
